// Admin: configure a single Help Centre — name/slug, branding, publish, and
// which portals appear as cards (ordered).
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, ArrowSquareOut, ArrowUp, ArrowDown, Plus, X } from '@phosphor-icons/react';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Choose how an uploaded image fills its frame: 'contain' (show whole image,
// best for logos) or 'cover' (crop to fill, best for wide hero photos).
function ImageFitToggle({ value = 'contain', onChange, testid }) {
  const opts = [{ key: 'contain', label: 'Fit' }, { key: 'cover', label: 'Fill' }];
  return (
    <div className="inline-flex items-center rounded-md bg-zinc-100 p-0.5 ring-1 ring-zinc-200" data-testid={testid}>
      {opts.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)}
          className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${value === o.key ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}
          data-testid={`${testid}-${o.key}`}>{o.label}</button>
      ))}
    </div>
  );
}

export default function HelpCentreConfigPage() {
  const { hcId } = useParams();
  const { getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [hc, setHc] = useState(null);
  const [allPortals, setAllPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // editable state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [intro, setIntro] = useState('');
  const [theme, setTheme] = useState('#2563eb');
  const [footer, setFooter] = useState('');
  const [published, setPublished] = useState(false);
  const [portalIds, setPortalIds] = useState([]);
  const [addId, setAddId] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [heroUrl, setHeroUrl] = useState('');
  const [logoFit, setLogoFit] = useState('contain');
  const [heroFit, setHeroFit] = useState('cover');
  const [uploading, setUploading] = useState(null); // 'logo' | 'hero' | null
  const logoRef = useRef(null);
  const heroRef = useRef(null);

  const sdEnabled = caps?.service_desk_enabled === true;

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/service-desk/help-centres/${hcId}`, { withCredentials: true, headers: getOrgHeaders() });
      setHc(data);
      setAllPortals(data.all_portals || []);
      setName(data.name || '');
      setSlug(data.slug || '');
      setIntro(data.branding?.intro_text || '');
      setTheme(data.branding?.theme_color || '#2563eb');
      setFooter(data.branding?.footer_text || '');
      setPublished(data.is_published === true);
      setPortalIds(data.portal_ids || []);
      setLogoUrl(data.branding?.logo_url || '');
      setHeroUrl(data.branding?.hero_image_url || '');
      setLogoFit(data.branding?.logo_fit || 'contain');
      setHeroFit(data.branding?.hero_fit || 'cover');
    } catch (e) { console.error(e); setHc({ error: true }); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, hcId, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (loading) return <div className="flex-1 flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!hc || hc.error) return <div className="flex-1 flex items-center justify-center text-zinc-400">Help centre not found</div>;

  const portalById = Object.fromEntries(allPortals.map(p => [p.portal_id, p]));
  const unselected = allPortals.filter(p => !portalIds.includes(p.portal_id));

  const move = (idx, dir) => {
    setPortalIds(prev => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const removePortal = (id) => setPortalIds(prev => prev.filter(x => x !== id));
  const addPortal = () => { if (addId && !portalIds.includes(addId)) { setPortalIds(prev => [...prev, addId]); setAddId(''); } };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/service-desk/help-centres/${hcId}/${kind}`, fd, { withCredentials: true, headers: getOrgHeaders() });
      let url = kind === 'logo' ? data.logo_url : data.hero_image_url;
      if (url) url = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
      if (kind === 'logo') setLogoUrl(url); else setHeroUrl(url);
      toast.success(`${kind === 'logo' ? 'Logo' : 'Hero image'} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/service-desk/help-centres/${hcId}`, {
        name, slug, is_published: published, portal_ids: portalIds,
        branding: { intro_text: intro, theme_color: theme, footer_text: footer, logo_url: logoUrl || null, hero_image_url: heroUrl || null, logo_fit: logoFit, hero_fit: heroFit },
      }, { withCredentials: true, headers: getOrgHeaders() });
      setHc(prev => ({ ...prev, ...data }));
      setSlug(data.slug);
      toast.success('Help centre saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="hc-config-page-root">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/service-desk/help-centres')} className="text-sm text-zinc-500 hover:text-zinc-800 flex items-center gap-1.5 mb-4" data-testid="hc-config-back">
          <ArrowLeft size={14} /> All help centres
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">{hc.name}</h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={published} onCheckedChange={setPublished} data-testid="hc-publish-toggle" />
              <span className="text-xs font-medium text-zinc-700">{published ? 'Live' : 'Off'}</span>
            </div>
            <Button variant="outline" size="sm" disabled={!hc.is_published} onClick={() => window.open(`/helpcentre/${hc.slug}`, '_blank')} data-testid="hc-open-public">
              <ArrowSquareOut size={13} className="mr-1.5" /> View
            </Button>
          </div>
        </div>

        {/* General */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-5 space-y-3">
          <h2 className="text-sm font-semibold text-zinc-900">General</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="hc-config-name" /></div>
            <div><Label className="text-xs">Slug (URL)</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="hc-config-slug" /></div>
          </div>
          <div><Label className="text-xs">Intro text</Label><Input value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Shown under the welcome headline" data-testid="hc-config-intro" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Theme colour</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={theme} onChange={(e) => setTheme(e.target.value)} className="h-9 w-12 rounded border border-zinc-200 p-0.5" data-testid="hc-config-theme" />
                <Input value={theme} onChange={(e) => setTheme(e.target.value)} className="flex-1" />
              </div>
            </div>
            <div><Label className="text-xs">Footer text</Label><Input value={footer} onChange={(e) => setFooter(e.target.value)} data-testid="hc-config-footer" /></div>
          </div>
          <p className="text-[11px] text-zinc-400">Public URL: <code>/helpcentre/{slug}</code></p>
        </div>

        {/* Branding images */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-5 space-y-4">
          <h2 className="text-sm font-semibold text-zinc-900">Branding images</h2>
          <div className="grid grid-cols-2 gap-4">
            {/* Logo */}
            <div>
              <Label className="text-xs">Logo</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-14 h-14 rounded-lg ring-1 ring-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden shrink-0">
                  {logoUrl ? <img src={logoUrl} alt="logo" className={`w-full h-full ${logoFit === 'cover' ? 'object-cover' : 'object-contain p-1'}`} data-testid="hc-logo-preview" />
                    : <div className="w-9 h-9 rounded flex items-center justify-center font-bold text-white" style={{ background: theme }}>{(name || 'H').charAt(0)}</div>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage('logo', e.target.files?.[0])} data-testid="hc-logo-file" />
                  <Button variant="outline" size="sm" className="h-8 text-xs" disabled={uploading === 'logo'} onClick={() => logoRef.current?.click()} data-testid="hc-logo-upload">
                    {uploading === 'logo' ? 'Uploading…' : (logoUrl ? 'Replace logo' : 'Upload logo')}
                  </Button>
                  {logoUrl && (
                    <div className="flex items-center gap-2">
                      <ImageFitToggle value={logoFit} onChange={setLogoFit} testid="hc-logo-fit" />
                      <button type="button" className="text-[11px] text-red-500 hover:text-red-700" onClick={() => setLogoUrl('')} data-testid="hc-logo-remove">Remove</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Hero */}
            <div>
              <Label className="text-xs">Hero background</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-24 h-14 rounded-lg ring-1 ring-zinc-200 bg-zinc-50 overflow-hidden shrink-0">
                  {heroUrl ? <img src={heroUrl} alt="hero" className={`w-full h-full ${heroFit === 'contain' ? 'object-contain p-1' : 'object-cover'}`} data-testid="hc-hero-preview" /> : <div className="w-full h-full" style={{ background: theme, opacity: 0.15 }} />}
                </div>
                <div className="flex flex-col gap-1.5">
                  <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage('hero', e.target.files?.[0])} data-testid="hc-hero-file" />
                  <Button variant="outline" size="sm" className="h-8 text-xs" disabled={uploading === 'hero'} onClick={() => heroRef.current?.click()} data-testid="hc-hero-upload">
                    {uploading === 'hero' ? 'Uploading…' : (heroUrl ? 'Replace hero' : 'Upload hero')}
                  </Button>
                  {heroUrl && (
                    <div className="flex items-center gap-2">
                      <ImageFitToggle value={heroFit} onChange={setHeroFit} testid="hc-hero-fit" />
                      <button type="button" className="text-[11px] text-red-500 hover:text-red-700" onClick={() => setHeroUrl('')} data-testid="hc-hero-remove">Remove</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-zinc-400">PNG/JPG up to 5MB. The logo shows in the header; the hero sits faded behind the welcome banner. Click Save to apply removals.</p>
        </div>

        {/* Portals */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-5">
          <h2 className="text-sm font-semibold text-zinc-900 mb-1">Portals shown as cards</h2>
          <p className="text-xs text-zinc-500 mb-3">Drag-free reorder with the arrows. Only PUBLISHED portals appear on the live help centre.</p>

          <div className="space-y-2 mb-4" data-testid="hc-selected-portals">
            {portalIds.length === 0 && <p className="text-xs text-zinc-400 italic">No portals added yet.</p>}
            {portalIds.map((id, idx) => {
              const p = portalById[id];
              if (!p) return null;
              return (
                <div key={id} className="flex items-center justify-between bg-zinc-50 rounded px-3 py-2" data-testid={`hc-portal-item-${id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-zinc-400 w-5">{idx + 1}.</span>
                    <span className="text-sm font-medium text-zinc-800 truncate">{p.name}</span>
                    <span className="text-[11px] text-zinc-400">/{p.subdomain}</span>
                    {!p.is_published && <span className="text-[10px] text-amber-600 font-medium">(draft — hidden)</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30" data-testid={`hc-move-up-${id}`}><ArrowUp size={14} /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === portalIds.length - 1} className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30" data-testid={`hc-move-down-${id}`}><ArrowDown size={14} /></button>
                    <button onClick={() => removePortal(id)} className="p-1 text-red-500 hover:text-red-700" data-testid={`hc-remove-portal-${id}`}><X size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>

          {unselected.length > 0 && (
            <div className="flex items-center gap-2">
              <select value={addId} onChange={(e) => setAddId(e.target.value)} className="flex-1 text-sm border border-zinc-300 rounded px-2 py-1.5" data-testid="hc-add-portal-select">
                <option value="">Add a portal…</option>
                {unselected.map(p => <option key={p.portal_id} value={p.portal_id}>{p.name} (/{p.subdomain})</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={addPortal} disabled={!addId} data-testid="hc-add-portal-btn"><Plus size={13} className="mr-1" /> Add</Button>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="hc-save">{saving ? 'Saving…' : 'Save help centre'}</Button>
        </div>
      </div>
    </div>
  );
}
